<template>
  <section class="parking-complex-map" aria-label="Карта парковочных комплексов">
    <v-map
      ref="map"
      class="parking-complex-map__leaflet"
      :center="mapCenter"
      :max-zoom="20"
      :min-zoom="2"
      :options="mapOptions"
      :zoom="mapZoom"
      @ready="handleMapReady"
    >
      <v-tile-layer v-if="mapTilesUrl" :url="mapTilesUrl" />

      <v-polygon
        v-for="mapParking in mapParkings"
        :key="mapParking.key"
        :lat-lngs="mapParking.latLngs"
        :color="getPolygonStroke(mapParking)"
        :fill-color="mapParking.color"
        :fill-opacity="getPolygonFillOpacity(mapParking)"
        :weight="getPolygonWeight(mapParking)"
        @click="selectMapParking(mapParking)"
      />
    </v-map>

    <aside class="parking-complex-map__sidebar" aria-label="Парковочные комплексы на карте">
      <div v-if="complexes.length" class="parking-complex-map__complexes">
        <article
          v-for="parkingComplex in complexes"
          :key="parkingComplex.id || parkingComplex.name"
          class="parking-complex-map__complex"
          :class="{'is-expanded': parkingComplex.id === expandedComplexId}"
        >
          <button
            class="parking-complex-map__complex-header"
            type="button"
            :aria-expanded="String(parkingComplex.id === expandedComplexId)"
            @click="$emit('toggle-complex', parkingComplex)"
          >
            <strong>{{ getComplexTitle(parkingComplex) }}</strong>
            <svg aria-hidden="true" viewBox="0 0 20 20">
              <path d="M5.5 12.5L10 8l4.5 4.5" />
            </svg>
          </button>

          <div
            v-if="parkingComplex.id === expandedComplexId"
            class="parking-complex-map__parkings"
          >
            <ParkingSummaryCard
              v-for="parking in getComplexParkings(parkingComplex)"
              :key="parking.id || parking.name"
              :parking="parking"
              :selected="parking.id === mapSelectedParkingId"
              @select="selectParking(parkingComplex, $event)"
              @edit="$emit('edit-parking', {parkingComplex, parking: $event})"
              @delete="$emit('delete-parking', {parkingComplex, parking: $event})"
            />

            <p v-if="!getComplexParkings(parkingComplex).length" class="parking-complex-map__empty">
              В комплексе пока нет парковок
            </p>
          </div>
        </article>
      </div>

      <p v-else class="parking-complex-map__empty">
        Парковочных комплексов пока нет
      </p>
    </aside>
  </section>
</template>

<script>
import L from "leaflet";
import {LMap, LPolygon, LTileLayer} from "vue2-leaflet";
import ParkingSummaryCard from "@/components/pacs/analytics/parkingDetection/ParkingSummaryCard.vue";
import {PARKING_COLORS} from "@/components/pacs/analytics/parkingDetection/parkingComplexConfig.js";
import {MAP_TILES_URL} from "@/utils/consts.js";

const DEFAULT_CENTER = [0, 0];

const isCoordinate = value => Number.isFinite(Number(value));

/** Карта парковок с панелью комплексов. */
export default {
  name: "ParkingComplexMap",
  components: {
    ParkingSummaryCard,
    "v-map": LMap,
    "v-polygon": LPolygon,
    "v-tile-layer": LTileLayer,
  },
  props: {
    complexes: {
      type: Array,
      default: () => [],
    },
    expandedComplexId: {
      type: [Number, String],
      default: null,
    },
    selectedParkingId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      mapObject: null,
      mapOptions: {
        attributionControl: false,
        preferCanvas: true,
        zoomControl: false,
      },
      mapResizeTimer: null,
      mapSelectedParkingId: this.selectedParkingId,
      mapTilesUrl: MAP_TILES_URL,
    };
  },
  computed: {
    mapParkings() {
      return this.complexes.reduce((result, parkingComplex, complexIndex) => {
        this.getComplexParkings(parkingComplex).forEach((parking, parkingIndex) => {
          const latLngs = this.getParkingLatLngs(parking);
          if (latLngs.length < 3) return;

          result.push({
            color: this.getParkingColor(parking),
            complex: parkingComplex,
            key: `${parkingComplex.id || complexIndex}-${parking.id || parkingIndex}`,
            latLngs,
            parking,
          });
        });
        return result;
      }, []);
    },
    visibleMapParkings() {
      if (this.expandedComplexId === null) return this.mapParkings;
      const expandedParkings = this.mapParkings.filter(
        item => item.complex.id === this.expandedComplexId
      );
      return expandedParkings.length ? expandedParkings : this.mapParkings;
    },
    mapCenter() {
      const firstParking = this.visibleMapParkings[0];
      if (firstParking && firstParking.latLngs.length) return firstParking.latLngs[0];

      const complex = this.complexes.find(item => item.id === this.expandedComplexId)
        || this.complexes[0];
      const centroid = complex && complex.bbox_centroid;
      if (centroid && isCoordinate(centroid.lat) && isCoordinate(centroid.lon)) {
        return [Number(centroid.lat), Number(centroid.lon)];
      }
      return DEFAULT_CENTER;
    },
    mapZoom() {
      return this.visibleMapParkings.length ? 14 : 2;
    },
  },
  watch: {
    expandedComplexId() {
      this.$nextTick(this.fitVisibleParkings);
    },
    mapParkings() {
      this.$nextTick(this.fitVisibleParkings);
    },
  },
  beforeDestroy() {
    clearTimeout(this.mapResizeTimer);
  },
  methods: {
    getComplexParkings(parkingComplex) {
      return Array.isArray(parkingComplex.parkings) ? parkingComplex.parkings : [];
    },
    getComplexTitle(parkingComplex) {
      return parkingComplex.name || parkingComplex.title || "Без названия";
    },
    getParkingColor(parking) {
      const color = PARKING_COLORS.find(option => option.value === parking.color);
      return color ? color.hex : parking.color || PARKING_COLORS[0].hex;
    },
    getParkingLatLngs(parking) {
      if (Array.isArray(parking.polygon)) {
        const polygon = parking.polygon
          .filter(point => point && isCoordinate(point.lat) && isCoordinate(point.lon))
          .map(point => [Number(point.lat), Number(point.lon)]);
        if (polygon.length >= 3) return polygon;
      }

      const minLat = parking.polygon_min_lat;
      const maxLat = parking.polygon_max_lat;
      const minLon = parking.polygon_min_lon;
      const maxLon = parking.polygon_max_lon;
      if (![minLat, maxLat, minLon, maxLon].every(isCoordinate)) return [];

      return [
        [Number(minLat), Number(minLon)],
        [Number(minLat), Number(maxLon)],
        [Number(maxLat), Number(maxLon)],
        [Number(maxLat), Number(minLon)],
      ];
    },
    isSelectedParking(mapParking) {
      return mapParking.parking.id === this.mapSelectedParkingId;
    },
    getPolygonStroke(mapParking) {
      return this.isSelectedParking(mapParking) ? "#168EEA" : mapParking.color;
    },
    getPolygonWeight(mapParking) {
      return this.isSelectedParking(mapParking) ? 4 : 2;
    },
    getPolygonFillOpacity(mapParking) {
      if (this.isSelectedParking(mapParking)) return 0.4;
      return mapParking.complex.id === this.expandedComplexId ? 0.3 : 0.18;
    },
    handleMapReady(mapObject) {
      this.mapObject = mapObject;
      this.mapResizeTimer = setTimeout(() => {
        mapObject.invalidateSize();
        this.fitVisibleParkings();
      }, 80);
    },
    fitVisibleParkings() {
      if (!this.mapObject || !this.visibleMapParkings.length) return;

      const points = this.visibleMapParkings.reduce(
        (result, mapParking) => result.concat(mapParking.latLngs),
        []
      );
      const bounds = L.latLngBounds(points);
      if (!bounds.isValid()) return;

      this.mapObject.fitBounds(bounds, {
        maxZoom: 18,
        paddingTopLeft: [48, 48],
        paddingBottomRight: [390, 48],
      });
    },
    selectMapParking(mapParking) {
      if (mapParking.complex.id !== this.expandedComplexId) {
        this.$emit("toggle-complex", mapParking.complex);
      }
      this.mapSelectedParkingId = mapParking.parking.id;
    },
    selectParking(parkingComplex, parking) {
      this.mapSelectedParkingId = parking.id;
      this.$nextTick(() => {
        const mapParking = this.mapParkings.find(
          item => item.complex.id === parkingComplex.id && item.parking.id === parking.id
        );
        if (!mapParking || !this.mapObject) return;
        const bounds = L.latLngBounds(mapParking.latLngs);
        if (bounds.isValid()) this.mapObject.fitBounds(bounds, {maxZoom: 19, padding: [80, 80]});
      });
    },
  },
};
</script>
